import React from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Architecture as ArchitectureIcon,
  Engineering as EngineeringIcon,
  Business as BusinessIcon,
  Code as CodeIcon,
  Flight as FlightIcon,
  AccountBalance as AccountBalanceIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Payment as PaymentIcon,
  DeveloperMode as DeveloperModeIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import experiencesData from '../data/experiences.json';

interface Experience {
  id: number;
  period: string;
  role: string;
  company: string;
  location: string;
  description: string;
  icon: React.ReactNode;
}

const ExperienceSection: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Icon mapping function
  const getIconComponent = (iconName: string): React.ReactNode => {
    const iconMap: { [key: string]: React.ReactNode } = {
      Architecture: <ArchitectureIcon />,
      AccountBalance: <AccountBalanceIcon />,
      Business: <BusinessIcon />,
      Payment: <PaymentIcon />,
      Flight: <FlightIcon />,
      Engineering: <EngineeringIcon />,
      Storage: <StorageIcon />,
      Security: <SecurityIcon />,
      Code: <CodeIcon />,
      DeveloperMode: <DeveloperModeIcon />,
    };
    return iconMap[iconName] || <CodeIcon />;
  };

  // Process JSON data to include icon components
  const experiences: Experience[] = experiencesData.experiences.map(item => ({
    ...item,
    icon: getIconComponent(item.icon),
  }));

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <Box
      id="experience"
      sx={{
        py: { xs: 4, sm: 6, md: 8 },
        backgroundColor: isDark ? 'rgba(18, 18, 18, 0.95)' : 'rgba(248, 249, 250, 0.95)',
        minHeight: { xs: 'auto', md: '100vh' },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: isDark 
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.03) 100%)'
            : 'linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.02) 100%)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box textAlign="center" mb={{ xs: 4, sm: 5, md: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              className="section-heading-allura"
              sx={{
                fontWeight: 400,
                fontFamily: 'Allura, cursive !important',
                fontSize: { xs: '36px !important', sm: '42px !important', md: '48px !important' },
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #ffffff 0%, #cccccc 50%, #ffffff 100%)'
                  : 'linear-gradient(135deg, #000000 0%, #333333 50%, #000000 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: { xs: 1, md: 2 },
                '@media (min-width: 480px) and (max-width: 600px)': {
                  fontSize: '48px !important',
                },
                '@media (min-width: 601px) and (max-width: 960px)': {
                  fontSize: '56px !important',
                },
                '@media (min-width: 961px) and (max-width: 1280px)': {
                  fontSize: '64px !important',
                },
                '@media (min-width: 1281px)': {
                  fontSize: '72px !important',
                },
              }}
            >
              Professional Experience
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              className="section-description-allura"
              sx={{ 
                maxWidth: 600, 
                mx: 'auto',
                fontFamily: 'Allura, cursive !important',
                fontWeight: 400,
                fontSize: { xs: '20px !important', sm: '24px !important', md: '28px !important' },
                '@media (min-width: 480px) and (max-width: 600px)': {
                  fontSize: '28px !important',
                },
                '@media (min-width: 601px) and (max-width: 960px)': {
                  fontSize: '32px !important',
                },
                '@media (min-width: 961px) and (max-width: 1280px)': {
                  fontSize: '35px !important',
                },
                '@media (min-width: 1281px)': {
                  fontSize: '36px !important',
                },
              }}
            >
              My journey through technology leadership and digital transformation
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Box 
            sx={{
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 3,
              justifyContent: 'center',
              position: 'relative',
              '@keyframes flowLine': {
                '0%': {
                  opacity: 0.3,
                  transform: 'translateY(-50%) scaleX(0.5)',
                },
                '50%': {
                  opacity: 0.8,
                  transform: 'translateY(-50%) scaleX(1)',
                },
                '100%': {
                  opacity: 0.3,
                  transform: 'translateY(-50%) scaleX(0.5)',
                },
              },
              '@keyframes flowLineVertical': {
                '0%': {
                  opacity: 0.3,
                  transform: 'translateX(-50%) scaleY(0.5)',
              },
                '50%': {
                  opacity: 0.8,
                  transform: 'translateX(-50%) scaleY(1)',
                },
                '100%': {
                  opacity: 0.3,
                  transform: 'translateX(-50%) scaleY(0.5)',
                },
              },
            }}
          >
            {experiences.map((experience, index) => (
              <Box 
                key={experience.id}
                    sx={{
                  width: { xs: '100%', md: 'calc(50% - 12px)', lg: 'calc(33.333% - 16px)' },
                  maxWidth: 400,
                  position: 'relative',
                  zIndex: 2,
                  // Add connecting line after each card except the last one
                  '&::after': index < experiences.length - 1 ? {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    right: '-24px', // Extend beyond the card
                    width: '24px',
                    height: '2px',
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    animation: 'flowLine 2s ease-in-out infinite',
                    '@media (max-width: 900px)': {
                      display: 'none', // Hide on mobile
                    },
                    '@media (min-width: 901px) and (max-width: 1200px)': {
                      // On tablet, only show lines for 2-column layout (every 2nd card)
                      display: index % 2 === 0 ? 'block' : 'none',
                    },
                    '@media (min-width: 1201px)': {
                      // On desktop, only show lines for 3-column layout (every 3rd card)
                      display: index % 3 === 2 ? 'block' : 'none',
                    },
                  } : {},
                  // Add vertical connecting line for chronological flow
                  '&::before': index < experiences.length - 1 ? {
                    content: '""',
                    position: 'absolute',
                    bottom: '-24px',
                    left: '50%',
                    width: '2px',
                    height: '24px',
                    background: `linear-gradient(180deg, ${theme.palette.primary.main}, transparent)`,
                    transform: 'translateX(-50%)',
                    zIndex: 1,
                    animation: 'flowLineVertical 2s ease-in-out infinite',
                    '@media (max-width: 900px)': {
                      display: 'none', // Hide on mobile
                    },
                    '@media (min-width: 901px) and (max-width: 1200px)': {
                      // On tablet, show vertical lines for 2-column layout
                      display: index % 2 === 1 ? 'block' : 'none',
                    },
                    '@media (min-width: 1201px)': {
                      // On desktop, show vertical lines for 3-column layout
                      display: index % 3 === 2 ? 'block' : 'none',
                    },
                  } : {},
                        }}
              >
                <motion.div variants={cardVariants}>
                  <Card
                    sx={{
                      height: '100%',
                        background: isDark 
                          ? 'linear-gradient(135deg, rgba(26,26,34,0.95) 0%, rgba(34,34,44,0.95) 100%)'
                          : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,1) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: isDark 
                          ? '1px solid rgba(255,255,255,0.1)'
                          : '1px solid rgba(0,0,0,0.1)',
                        boxShadow: isDark 
                          ? '0 8px 32px rgba(0,0,0,0.2)'
                          : '0 8px 32px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                      cursor: 'pointer',
                        '&:hover': {
                        transform: 'translateY(-8px)',
                          boxShadow: isDark 
                          ? '0 16px 48px rgba(0,0,0,0.3)'
                          : '0 16px 48px rgba(0,0,0,0.2)',
                          border: isDark 
                          ? '1px solid rgba(255,255,255,0.3)'
                          : '1px solid rgba(0,0,0,0.3)',
                        },
                      }}
                    >
                    <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, md: 2 } }}>
                        <Box sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                          {experience.icon}
                        </Box>
                        <Chip 
                          label={experience.period}
                          size="small" 
                          sx={{ 
                            ml: 'auto', 
                            backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)', 
                            color: isDark ? '#ffffff' : theme.palette.text.primary, 
                            fontFamily: '"Roboto Mono", "Courier New", monospace',
                            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                            height: { xs: 20, md: 24 }
                          }}
                        />
                      </Box>
                      <Typography 
                        variant="h6" 
                        component="h3" 
                        sx={{ 
                          fontWeight: 600, 
                          mb: { xs: 0.5, md: 1 }, 
                          color: theme.palette.text.primary, 
                          fontFamily: '"Roboto Mono", "Courier New", monospace',
                          fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
                        }}
                      >
                        {experience.role}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        color="text.secondary" 
                        sx={{ 
                          mb: { xs: 0.5, md: 1 }, 
                          fontFamily: '"Roboto Mono", "Courier New", monospace',
                          fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' }
                        }}
                      >
                        {experience.company} - {experience.location}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          lineHeight: { xs: 1.5, md: 1.6 }, 
                          fontFamily: '"Roboto Mono", "Courier New", monospace',
                          fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' }
                        }}
                      >
                        {experience.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
                    </Box>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ExperienceSection; 